import ora from 'ora';
import chalk from 'chalk';
import fg from 'fast-glob';
import path from 'node:path';
import { execa } from 'execa';
import prompts from 'prompts';
import { Command } from 'commander';
import template from 'lodash.template';
import { existsSync, promises as fs } from 'node:fs';

import {
  logger,
  getRegistries,
  getContentRegistries,
  getMultiverseUIConfig,
  getProjectPackageManager,
  getSelectedAndDepsRegistries,
  orderRegistriesByImportHierarchy,
} from '../utils';
import { addOptionsSchema, componentNotFoundErrorSchema } from '../schema';

const PROJECT_SHARED_IGNORE = [
  '**/node_modules/**',
  '.next',
  'public',
  'dist',
  'build',
];

const pathNameRegex = new RegExp(/<%= path_to_([\S]*) %>/, 'g');

const add = new Command()
  .name('add')
  .description('add a component to your project')
  .argument('[components...]', 'the components to add')
  .option(
    '-c, --cwd <cwd>',
    'the working directory. defaults to the current directory.',
    process.cwd()
  )
  .option('-o, --out <out>', 'the path to add the component to.')
  .action(async (components, opts) => {
    try {
      // -----------------------------------------------------------------------
      // Handle Invalid Component Name Input (e.g. multiverse-ui@latest add <InvalidComponentName>)
      // -----------------------------------------------------------------------
      const componentNamesParsingResult =
        addOptionsSchema.shape.components.safeParse(components);
      if (componentNamesParsingResult.error) {
        const resultError = componentNotFoundErrorSchema.safeParse(
          componentNamesParsingResult.error
        );
        if (resultError.error) throw new Error();
        const receivedValues = new Set();
        resultError.data.issues[0].unionErrors?.forEach((unionError) => {
          unionError.issues.forEach((issue) => {
            receivedValues.add(issue.received);
          });
        });
        const uniqueReceivedValues = [...receivedValues];
        return logger.warn(
          `Cannot find ${chalk.green(uniqueReceivedValues.join(', '))} in the registry.`
        );
      }
      // -----------------------------------------------------------------------

      const options = addOptionsSchema.parse({ components, ...opts });
      const cwd = path.resolve(options.cwd);

      if (!existsSync(cwd)) {
        logger.error(`The path ${cwd} does not exist. Please try again.`);
        process.exit(1);
      }

      const config = await getMultiverseUIConfig(cwd);
      if (!config) {
        logger.warn(
          `Configuration is missing. Please run ${chalk.green(
            `init`
          )} to create a multiverse.config.json file.`
        );
        process.exit(1);
      }

      let selectedComponents = options.components;

      if (!selectedComponents?.length) {
        logger.warn(
          'No components selected. Browse https://multiverse-design-system.multisyscorp.io to see all the available components. Exiting.'
        );
        process.exit(0);
      }

      // -----------------------------------------------------------------------
      // Get all the selected component names including their registry dependency
      // -----------------------------------------------------------------------
      const registryCalatog = await getRegistries();
      const selectedAndDepRegistries = getSelectedAndDepsRegistries(
        selectedComponents.map(
          (name) => registryCalatog.find((r) => r.name === name)!
        ),
        registryCalatog
      );

      const orderedSelectedAndDepRegistries = orderRegistriesByImportHierarchy(
        selectedAndDepRegistries
      );

      const componentNamesToBeAdded = orderedSelectedAndDepRegistries.map(
        (r) => r.name
      );

      const selectedComponentAndRegistryDepsRegistries =
        await getContentRegistries(componentNamesToBeAdded);

      if (!selectedComponentAndRegistryDepsRegistries?.length) {
        logger.warn(
          'Selected components not found. Browse https://multiverse-design-system.multisyscorp.io to see all the available components. Exiting.'
        );
        process.exit(0);
      }

      const { proceed } = await prompts({
        type: 'confirm',
        name: 'proceed',
        message: `Ready to install components and dependencies. Proceed?`,
        initial: true,
      });

      if (!proceed) {
        process.exit(0);
      }

      const spinner = ora(`Installing components...`).start();

      // -----------------------------------------------------------------------------
      // This is where the components will go
      // -----------------------------------------------------------------------------
      const targetDir = options.out
        ? path.resolve(options.out)
        : config.resolvedPaths.components;
      if (!targetDir) return;
      if (!existsSync(targetDir))
        await fs.mkdir(targetDir, { recursive: true });
      // -----------------------------------------------------------------------

      // -----------------------------------------------------------------------------
      // Looping all the components to be added
      // -----------------------------------------------------------------------------
      registryLoop: for (const componentRegistry of selectedComponentAndRegistryDepsRegistries) {
        spinner.text = `Installing ${componentRegistry.name}...`;

        const existingComponentPath = fg.globSync(
          `**/${componentRegistry.name}.{js,jsx,ts,tsx}`,
          {
            cwd,
            deep: 10,
            ignore: PROJECT_SHARED_IGNORE,
          }
        )[0] as string | undefined;

        if (existingComponentPath) {
          spinner.stop();
          const { overwrite } = await prompts({
            type: 'confirm',
            name: 'overwrite',
            message: `Component ${componentRegistry.name} already exists. Would you like to overwrite?`,
            initial: false,
          });

          if (!overwrite) {
            logger.info(
              `Skipped ${componentRegistry.name}. To overwrite, run with the ${chalk.green(
                '--overwrite'
              )} flag.`
            );
            continue registryLoop;
          }
        }

        spinner.start(`Installing ${componentRegistry.name}...`);

        for (const file of componentRegistry.files) {
          const filePath = existingComponentPath
            ? path.resolve(path.dirname(existingComponentPath), file.name)
            : path.resolve(targetDir, file.name);

          // -----------------------------------------------------------------------------
          // It looks like this;
          //  ["Button", "utilities"]
          // -----------------------------------------------------------------------------
          let match: RegExpExecArray | null;
          let componentLocalImportDependencyNames: string[] = [];
          while ((match = pathNameRegex.exec(file.content)) !== null) {
            componentLocalImportDependencyNames.push(match[1]);
          }
          componentLocalImportDependencyNames = Array.from(
            new Set(componentLocalImportDependencyNames)
          );
          // -------------------------------------------------------------------

          // -----------------------------------------------------------------------------
          // After the for loop below, it will looks like this;
          //  {
          //    path_to_Button: "./Button",
          //    path_to_utilities: "../../utilities"
          //  }
          // -----------------------------------------------------------------------------
          const templateData: Record<string, string> = {};
          for (const localImportDependencyName of componentLocalImportDependencyNames) {
            const localImportDependencyPathMatches = await fg.glob(
              `**/${localImportDependencyName}.{js,jsx,ts,tsx}`,
              {
                cwd,
                deep: 10,
                ignore: PROJECT_SHARED_IGNORE,
              }
            );

            const localImportDependencyPath =
              localImportDependencyPathMatches[0];

            if (localImportDependencyPath) {
              const dependencyFilePath = path.resolve(
                localImportDependencyPath
              );
              templateData[`path_to_${localImportDependencyName}`] = path
                .relative(filePath, dependencyFilePath)
                .replace(/\.[tj]sx?$/, '')
                .replace(/\\/g, '/')
                .replace('.', '');
            } else {
              templateData[`path_to_${localImportDependencyName}`] =
                `path_to_${localImportDependencyName}`;
            }
          }

          // -------------------------------------------------------------------
          // Set local import paths
          // -------------------------------------------------------------------
          let resolvedContent = template(file.content, {
            interpolate: /<%=([\s\S]+?)%>/g,
          })(templateData);
          // -------------------------------------------------------------------

          // -------------------------------------------------------------------
          // For tailwind v4 - Add the global css path import in css files
          // -------------------------------------------------------------------
          if (
            config.tailwind.version === 'v4' &&
            path.extname(file.name) === '.css'
          ) {
            const relativeGlobalCssPath = path
              .relative(
                filePath,
                path.resolve(config.resolvedPaths.tailwindCss)
              )
              .replace(/\\/g, '/')
              .replace('.', '');

            resolvedContent =
              `@import "${relativeGlobalCssPath}";\n\n` + resolvedContent;
          }
          // -------------------------------------------------------------------

          // -------------------------------------------------------------------
          // TODO: Run transformers. (e.g. Handle removing of TS types for JS Config)
          // -------------------------------------------------------------------
          //   const content = await transform({
          //     filename: file.name,
          //     raw: file.content,
          //     config,
          //   });
          // if (!config.tsx) {
          //   filePath = filePath.replace(/\.tsx$/, '.jsx');
          //   filePath = filePath.replace(/\.ts$/, '.js');
          // }

          await fs.writeFile(filePath, resolvedContent);
        }

        const packageManager = await getProjectPackageManager(cwd);
        // -----------------------------------------------------------------------------
        // Installs component library dependencies.
        // -----------------------------------------------------------------------------
        if (componentRegistry.dependencies?.length) {
          await execa(
            packageManager,
            [
              packageManager === 'npm' ? 'install' : 'add',
              ...componentRegistry.dependencies,
            ],
            {
              cwd,
            }
          );
        }
        // ---------------------------------------------------------------------

        // -----------------------------------------------------------------------------
        // Install devDependencies.
        // -----------------------------------------------------------------------------
        if (componentRegistry.devDependencies?.length) {
          await execa(
            packageManager,
            [
              packageManager === 'npm' ? 'install' : 'add',
              '-D',
              ...componentRegistry.devDependencies,
            ],
            {
              cwd,
            }
          );
        }
        // ---------------------------------------------------------------------
      }
      spinner.succeed(`Done.`);
    } catch (error) {
      if (typeof error === 'string') {
        logger.error(error);
        process.exit(1);
      }

      if (error instanceof Error) {
        logger.error(error.message);
        process.exit(1);
      }

      logger.error('Something went wrong. Please try again.');
      process.exit(1);
    }
  });

export default add;
